import React, { forwardRef, InputHTMLAttributes } from 'react';
import { motion } from 'framer-motion';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  id?: string;
  label?: string;
  tipo?: React.HTMLInputTypeAttribute;
  placeholder?: string;
  value?: string | number;
  onChange?: (evento: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  containerClassName?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      label,
      tipo = 'text',
      placeholder,
      value,
      onChange,
      className = '',
      disabled = false,
      required = false,
      error,
      containerClassName = '',
      ...rest
    },
    ref
  ) => {
    const inputId = id || React.useId();

    const baseClasses = `
      flex-1
      w-full
      px-4
      py-2
      text-text
      bg-background
      border
      border-gray-700
      rounded-lg
      focus:outline-none
      transition-all
      duration-200
      ease-in-out
      placeholder-gray-500
      disabled:opacity-50
      disabled:cursor-not-allowed
      shadow-sm
      hover:border-primary
    `;

    const errorClasses = error ? 'border-danger focus:ring-danger focus:border-danger' : 'focus:ring-2 focus:ring-primary focus:border-primary';

    return (
      <div className={`relative flex flex-col gap-1 ${containerClassName}`}>
        {label && (
          <label
            htmlFor={inputId}
            className={`
              text-sm
              font-medium
              text-text
              mb-1
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
              ${error ? 'text-danger' : ''}
            `}
          >
            {label}
            {required && <span className="text-danger ml-1">*</span>}
          </label>
        )}
        <motion.input
          ref={ref}
          id={inputId}
          type={tipo}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          disabled={disabled}
          required={required}
          className={`${baseClasses} ${errorClasses} ${className}`}
          aria-invalid={!!error}
          aria-describedby={error ? `${inputId}-error` : undefined}
          whileFocus={{ scale: 1.01 }}
          transition={{ type: "spring", stiffness: 400, damping: 20 }}
          {...rest}
        />
        {error && (
          <motion.p
            id={`${inputId}-error`}
            className="text-danger text-xs mt-1"
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.2 }}
          >
            {error}
          </motion.p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;